import React, { useState, useEffect } from 'react';
import { Compass, ArrowLeft, Sparkles, Plus } from 'lucide-react';
import { Button } from '../components/Button';
import { cn } from '../utils/cn';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import type { CoreValue } from '../types';
import { ProtectedLayout } from '../components/ProtectedLayout';

const coreValuesList: CoreValue[] = [
  'Achievement', 'Authenticity', 'Clarity', 'Connection', 'Contribution', 'Courage',
  'Discipline', 'Excellence', 'Faith', 'Family', 'Friendship',
  'Growth', 'Hustle', 'Impact', 'Integrity', 'Kindness', 'Leadership', 'Loyalty',
  'Mastery', 'Passion', 'Presence', 'Purpose', 'Resilience', 'Responsibility',
  'Service', 'Structures', 'Success', 'Systems', 'Teaching', 'Vision'
];

const sparkWordsList: CoreValue[] = [
  'Adventure', 'Boldness', 'Creativity', 'Curiosity',
  'Dancing', 'Discovery', 'Energy', 'Exercise', 'Exploration',
  'Flow', 'Fun', 'Imagination', 'Laughter', 'Momentum', 'Movement', 'Music', 'Nature',
  'Play', 'Pleasure', 'Spontaneity', 'Vitality', 'Wonder'
];

type SelectedValue = { value: string; group: 'cv' | 'sw' };

const sparkWordsSet = new Set<string>(sparkWordsList);

export function UpdateCoreValues() {
  const navigate = useNavigate();
  const location = useLocation();

  const [selectedValues, setSelectedValues] = useState<SelectedValue[]>([]);
  const [customCV, setCustomCV] = useState('');
  const [customSW, setCustomSW] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    async function fetchCurrentValues() {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.user) { navigate('/'); return; }

        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('core_value_1, core_value_2, core_value_3')
          .eq('id', session.user.id)
          .single();

        if (profileError) throw profileError;

        if (profile) {
          const raw = [profile.core_value_1, profile.core_value_2, profile.core_value_3].filter(Boolean) as string[];
          setSelectedValues(raw.map(v => ({
            value: v,
            group: sparkWordsSet.has(v) ? 'sw' : 'cv'
          })));
        }
      } catch (error: any) {
        console.error('Error fetching core values:', error);
        setError(error.message);
      } finally {
        setIsLoading(false);
      }
    }

    fetchCurrentValues();
  }, [navigate]);

  const hasSparkWord = selectedValues.some(sv => sv.group === 'sw');
  const isValid = selectedValues.length === 3 && hasSparkWord;

  const handleSelect = (value: string, group: 'cv' | 'sw') => {
    const exists = selectedValues.find(sv => sv.value === value);
    if (exists) {
      setSelectedValues(prev => prev.filter(sv => sv.value !== value));
    } else if (selectedValues.length < 3) {
      setSelectedValues(prev => [...prev, { value, group }]);
    }
  };

  const handleAddCustom = (group: 'cv' | 'sw') => {
    const raw = group === 'cv' ? customCV : customSW;
    const trimmed = raw.trim();
    if (!trimmed || selectedValues.length >= 3) return;
    if (selectedValues.find(sv => sv.value.toLowerCase() === trimmed.toLowerCase())) return;
    setSelectedValues(prev => [...prev, { value: trimmed, group }]);
    if (group === 'cv') setCustomCV('');
    else setCustomSW('');
  };

  const sendWebhook = async () => {
    try {
      const webhookUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ghl-webhook`;
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !session?.user) return;

      const res = await fetch(webhookUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: session.user.id })
      });
      if (!res.ok) console.error('Webhook failed:', await res.text());
    } catch (err) {
      console.error('Webhook error:', err);
    }
  };

  const handleUpdate = async () => {
    if (!isValid || isSubmitting) return;
    setIsSubmitting(true);
    setError('');

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) throw new Error('Not authenticated');

      const values = selectedValues.map(sv => sv.value);
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ core_value_1: values[0], core_value_2: values[1], core_value_3: values[2] })
        .eq('id', session.user.id);

      if (updateError) throw updateError;
      await sendWebhook();

      const returnPath = location.state?.from === '/profile' ? '/profile' : '/dashboard';
      navigate(returnPath);
    } catch (error: any) {
      console.error('Error updating core values:', error);
      setError(error.message || 'Failed to update core values');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-white to-blue-50 flex items-center justify-center">
        <div className="text-[#236caf]">Loading...</div>
      </div>
    );
  }

  const ValueChip = ({ value, group }: { value: string; group: 'cv' | 'sw' }) => {
    const isSelected = !!selectedValues.find(sv => sv.value === value);
    const isDisabled = selectedValues.length === 3 && !isSelected;

    return (
      <button
        onClick={() => handleSelect(value, group)}
        disabled={isDisabled}
        className={cn(
          'px-4 py-2 rounded-full text-sm font-medium border-2 transition-all duration-150 select-none',
          group === 'cv'
            ? isSelected
              ? 'bg-[#004060] border-[#004060] text-white shadow-sm'
              : 'border-[#004060]/30 text-[#004060] hover:border-[#004060] hover:bg-[#004060]/5'
            : isSelected
              ? 'bg-amber-500 border-amber-500 text-white shadow-sm'
              : 'border-amber-400/50 text-amber-700 hover:border-amber-400 hover:bg-amber-50',
          isDisabled && 'opacity-40 cursor-not-allowed'
        )}
      >
        {value}
      </button>
    );
  };

  const CustomInput = ({ group }: { group: 'cv' | 'sw' }) => {
    const value = group === 'cv' ? customCV : customSW;
    const setter = group === 'cv' ? setCustomCV : setCustomSW;
    const disabled = selectedValues.length >= 3;

    return (
      <div className={cn(
        'flex items-center gap-2 px-3 py-1.5 rounded-full border-2 border-dashed transition-all',
        group === 'cv' ? 'border-[#004060]/30' : 'border-amber-400/50',
        disabled && 'opacity-40'
      )}>
        <input
          type="text"
          value={value}
          onChange={e => setter(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddCustom(group); } }}
          placeholder="Other..."
          disabled={disabled}
          className={cn(
            'bg-transparent text-sm font-medium outline-none w-24 placeholder-gray-400',
            group === 'cv' ? 'text-[#004060]' : 'text-amber-700'
          )}
        />
        <button
          onClick={() => handleAddCustom(group)}
          disabled={disabled || !value.trim()}
          className={cn(
            'rounded-full transition-colors',
            group === 'cv' ? 'text-[#004060]' : 'text-amber-600',
            (!value.trim() || disabled) && 'opacity-30 cursor-not-allowed'
          )}
        >
          <Plus className="w-4 h-4" />
        </button>
      </div>
    );
  };

  const validMsg = selectedValues.length === 3 && !hasSparkWord
    ? 'Please select at least one Spark Word.'
    : null;

  return (
    <ProtectedLayout>
      <div className="min-h-screen flex flex-col items-center justify-center p-6 bg-gradient-to-b from-white to-blue-50">
        <div className="max-w-3xl w-full bg-white rounded-xl shadow-lg p-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Compass className="w-8 h-8 text-[#236caf]" />
              <h1 className="text-3xl font-bold text-[#004060]">Update Core Values</h1>
            </div>
            <Button
              variant="outline"
              onClick={() => navigate(location.state?.from || '/dashboard')}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </Button>
          </div>

          <p className="text-gray-600 mb-2">Choose 3 values that represent you.</p>
          <p className="text-sm text-gray-500 mb-8">
            Select <strong>3 total</strong> — you must include <strong>at least one Spark Word</strong>.
            Valid combinations: 2 Core Values + 1 Spark Word, 1 Core Value + 2 Spark Words, or 3 Spark Words.
          </p>

          {error && (
            <div className="mb-6 p-4 bg-red-50 text-red-700 rounded-lg text-sm">{error}</div>
          )}

          {/* Core Values group */}
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-3 h-3 rounded-full bg-[#004060]" />
              <h2 className="text-base font-semibold text-[#004060] uppercase tracking-wide">Core Values</h2>
            </div>
            <div className="flex flex-wrap gap-2">
              {coreValuesList.map(v => <ValueChip key={v} value={v} group="cv" />)}
              <CustomInput group="cv" />
            </div>
          </div>

          {/* Spark Words group */}
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-4 h-4 text-amber-500" />
              <h2 className="text-base font-semibold text-amber-600 uppercase tracking-wide">Spark Words</h2>
              <span className="text-xs text-amber-500 font-medium">(at least 1 required)</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {sparkWordsList.map(v => <ValueChip key={v} value={v} group="sw" />)}
              <CustomInput group="sw" />
            </div>
          </div>

          {/* Selection summary */}
          {selectedValues.length > 0 && (
            <div className="mb-6 p-4 bg-blue-50 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-gray-700">
                  Selected ({selectedValues.length}/3)
                </h3>
                {validMsg && <span className="text-xs text-amber-600 font-medium">{validMsg}</span>}
              </div>
              <div className="flex flex-wrap gap-2">
                {selectedValues.map(({ value, group }) => (
                  <div
                    key={value}
                    className={cn(
                      'px-3 py-1 rounded-full border flex items-center gap-1.5 text-sm font-medium',
                      group === 'sw'
                        ? 'bg-amber-50 border-amber-400 text-amber-700'
                        : 'bg-white border-[#236caf] text-[#236caf]'
                    )}
                  >
                    {group === 'sw' && <Sparkles className="w-3 h-3" />}
                    {value}
                    <button
                      onClick={() => setSelectedValues(prev => prev.filter(sv => sv.value !== value))}
                      className="opacity-60 hover:opacity-100 leading-none"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <Button
            onClick={handleUpdate}
            disabled={!isValid || isSubmitting}
            className={cn('w-full', (!isValid || isSubmitting) && 'opacity-50 cursor-not-allowed')}
          >
            {isSubmitting ? 'Updating...' : 'Update Values'}
          </Button>
        </div>
      </div>
    </ProtectedLayout>
  );
}
